import {
  Button,
  Checkbox,
  Flex,
  Heading,
  IconAddLine,
  IconArrowOpenDownLine,
  IconArrowOpenUpLine,
  IconButton,
  IconInfoSolid,
  IconTrashLine,
  Text,
  TextInput,
  Tooltip,
  View,
} from "@instructure/ui";
import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import generateUuid from "../../../../utils/Uuid";
import { Card } from "../../../Card";
import RichTextEditor, {
  rteHasContent,
} from "../../../RichTextArea/RichTextEditor";
import RichTextViewer from "../../../RichTextArea/RichTextViewer";

/**
 * StructuredQuestionEditor — Allows coordinators to add, edit, reorder, and
 * delete structured questions within a Qualitative Measure form.
 *
 * Each question has:
 * - question_text (plain text input)
 * - helper_text (RCE / Lexical rich text)
 * - file_upload_enabled (toggle, default false)
 * - sequence (derived from array position)
 *
 * Props:
 * @param {Array}    questions    — Array of question objects [{id, question_text, helper_text, file_upload_enabled}]
 * @param {Function} setQuestions — State setter for the questions array
 *
 * Ref:
 * - validate() — Returns true if all questions have non-empty question_text; sets inline errors otherwise.
 */
const StructuredQuestionEditor = forwardRef(
  ({ questions, setQuestions, inTemplate }, ref) => {
    const { t } = useTranslation();
    const [errors, setErrors] = useState({});
    const inputRefs = useRef({});

    const handleAddQuestion = useCallback(() => {
      setQuestions((prev) => [
        ...prev,
        {
          key: generateUuid(),
          question_text: "",
          helper_text: "",
          file_upload_enabled: false,
        },
      ]);
    }, [setQuestions]);

    function validate() {
      // Sync any unblurred input values to state before validating
      const currentQuestions = questions.map((question) => {
        const key = question.id || question.key;
        const inputEl = inputRefs.current[key];
        if (inputEl) {
          return { ...question, question_text: inputEl.value };
        }
        return question;
      });
      setQuestions(currentQuestions);

      const newErrors = {};
      currentQuestions.forEach((question, index) => {
        if (!question.question_text || question.question_text.trim() === "") {
          newErrors[question.id || question.key || index] = t(
            "structuredQuestions.questionTextRequired"
          );
        }
      });
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }

    useImperativeHandle(ref, () => ({ validate }), [questions]);

    const clearError = useCallback((questionKey) => {
      setErrors((prev) => {
        if (!prev[questionKey]) return prev;
        const next = { ...prev };
        delete next[questionKey];
        return next;
      });
    }, []);

    const registerInputRef = useCallback((questionKey, el) => {
      inputRefs.current[questionKey] = el;
    }, []);

    return (
      <View as="div">
        <View as="div" margin="0 0 small 0">
          <Heading as="h3" level="h5" border="bottom" margin="0 0 small 0">
            {t("structuredQuestions.structuredQuestions")}
          </Heading>
        </View>
        {questions.map((question, index) => (
          <QuestionItem
            key={question.id || question.key}
            questionKey={question.id || question.key}
            question={question}
            index={index}
            isFirst={index === 0}
            isLast={index === questions.length - 1}
            setQuestions={setQuestions}
            error={errors[question.id || question.key || index]}
            clearError={clearError}
            registerInputRef={registerInputRef}
            inTemplate={inTemplate}
          />
        ))}
        {!inTemplate && (
          <Button
            color="primary"
            display="block"
            renderIcon={IconAddLine}
            onClick={handleAddQuestion}
          >
            {t("structuredQuestions.addQuestion")}
          </Button>
        )}
      </View>
    );
  }
);

/**
 * QuestionItem — Renders a single structured question form within the editor.
 * Memoized to prevent re-renders when sibling questions change.
 */
const QuestionItem = memo(
  ({
    questionKey,
    question,
    index,
    isFirst,
    isLast,
    setQuestions,
    error,
    clearError,
    registerInputRef,
    inTemplate,
  }) => {
    const { t } = useTranslation();
    const defaultHelperText = useRef(question.helper_text || "");
    const readOnly = inTemplate || question.has_response;

    function moveUp() {
      setQuestions((prev) =>
        prev.map((q, i) =>
          i === index - 1 ? prev[index] : i === index ? prev[index - 1] : q
        )
      );
    }

    function moveDown() {
      setQuestions((prev) =>
        prev.map((q, i) =>
          i === index + 1 ? prev[index] : i === index ? prev[index + 1] : q
        )
      );
    }

    function handleDelete() {
      setQuestions((prev) => prev.filter((_, i) => i !== index));
    }

    function handleQuestionTextChange(value) {
      if (value !== question.question_text) {
        setQuestions((prev) =>
          prev.map((q, i) => (i === index ? { ...q, question_text: value } : q))
        );
      }
    }

    function handleHelperTextChange(value) {
      const helperText = rteHasContent(value) ? value : "";
      setQuestions((prev) =>
        prev.map((q, i) =>
          i === index ? { ...q, helper_text: helperText } : q
        )
      );
    }

    function handleFileUploadToggle(e) {
      setQuestions((prev) =>
        prev.map((q, i) =>
          i === index ? { ...q, file_upload_enabled: e.target.checked } : q
        )
      );
    }

    return (
      <Card
        style={{
          backgroundColor: "#F1F1F1",
          border: "1px solid #ccc",
          height: "auto",
          margin: "0.75rem 0",
        }}
      >
        <Flex wrap="wrap" gap="small">
          <Flex.Item size="100%">
            {readOnly ? (
              <Flex>
                <Flex.Item shouldGrow shouldShrink>
                  <Text size="small" weight="bold">
                    {question.question_text}
                  </Text>
                </Flex.Item>
                {question.has_response && !inTemplate && (
                  <Flex.Item align="start">
                    <Tooltip
                      renderTip={t("structuredQuestions.hasResponseInfo")}
                    >
                      <IconInfoSolid color="brand" />
                    </Tooltip>
                  </Flex.Item>
                )}
              </Flex>
            ) : (
              <TextInput
                renderLabel={
                  <Flex gap="small">
                    <Flex.Item shouldGrow>
                      <Text size="small" weight="bold">
                        {t("structuredQuestions.questionLabel", {
                          number: index + 1,
                        })}
                      </Text>
                    </Flex.Item>
                    <Flex.Item>
                      <IconButton
                        color="primary-inverse"
                        screenReaderLabel={t(
                          "structuredQuestions.moveQuestionUp"
                        )}
                        size="small"
                        renderIcon={IconArrowOpenUpLine}
                        onClick={moveUp}
                        disabled={isFirst}
                      />
                    </Flex.Item>
                    <Flex.Item>
                      <IconButton
                        color="primary-inverse"
                        screenReaderLabel={t(
                          "structuredQuestions.moveQuestionDown"
                        )}
                        size="small"
                        renderIcon={IconArrowOpenDownLine}
                        onClick={moveDown}
                        disabled={isLast}
                      />
                    </Flex.Item>
                    <Flex.Item>
                      <IconButton
                        color="danger"
                        screenReaderLabel={t(
                          "structuredQuestions.deleteQuestion"
                        )}
                        size="small"
                        renderIcon={IconTrashLine}
                        onClick={handleDelete}
                      />
                    </Flex.Item>
                  </Flex>
                }
                defaultValue={question.question_text}
                inputRef={(el) => registerInputRef(questionKey, el)}
                onBlur={(e) => handleQuestionTextChange(e.target.value)}
                onFocus={() => {
                  if (error) clearError(questionKey);
                }}
                messages={error ? [{ text: error, type: "error" }] : []}
                placeholder={t("structuredQuestions.questionPlaceholder")}
              />
            )}
          </Flex.Item>
          {readOnly ? (
            rteHasContent(defaultHelperText.current) && (
              <Flex.Item size="100%">
                <RichTextViewer
                  id={`structured-question-helper-${questionKey}`}
                  ariaLabel={t("structuredQuestions.helperText")}
                  value={defaultHelperText.current}
                />
              </Flex.Item>
            )
          ) : (
            <Flex.Item size="100%">
              <RichTextEditor
                id={`structured-question-helper-${questionKey}`}
                label={
                  <Text weight="bold" size="small">
                    {t("structuredQuestions.helperText")}
                    <Text fontStyle="italic" size="small">
                      {" "}
                      ({t("form.optional")})
                    </Text>
                  </Text>
                }
                defaultValue={defaultHelperText.current}
                onChange={handleHelperTextChange}
              />
            </Flex.Item>
          )}
          <Flex.Item size="100%">
            <Checkbox
              label={t("structuredQuestions.enableFileUpload")}
              checked={question.file_upload_enabled}
              variant="toggle"
              size="small"
              onChange={handleFileUploadToggle}
              disabled={readOnly}
            />
          </Flex.Item>
        </Flex>
      </Card>
    );
  }
);

// Export validate as accessible via ref
export default StructuredQuestionEditor;
export { QuestionItem };
